import React, { useEffect, useState } from 'react';
import { useRouteMatch, useLocation, matchPath } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { HeadingText60, pxToRem } from '@modernhealth/vital-components';
import { useDispatch } from 'react-redux';
import {
    useConversations,
    RequestStateType,
    useMessagingClient,
} from '@modernhealth/messaging-kit';

import { navigationService } from 'src/config/rootService';
import { Route } from '@modernhealth/web-analytics';
import { SidebarLayout } from 'src/shared/layout/SidebarLayout';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { ProviderType } from 'src/modules/user/models/user';
import { CareCard } from 'src/modules/care/components/CareCard';
import { ConversationSubRoute, ConversationSubRouteParams } from './ConversationSubRoute';
import { ConversationListItem } from '../components/ConversationListItem';
import { NoConversations } from '../components/NoConversations';
import { GenericError } from '../components/GenericError';
import { Dispatch } from 'src/config/rootStore';
import coachPlaceholder from '../assets/coach_placeholder.png';

const UnorderedList = styled.ul`
    margin: ${pxToRem(10, 0)};
    padding: 0;
`;

const CareCardWrapper = styled.div`
    margin: ${pxToRem(11)};
    margin-top: auto;
`;

interface MessagesPageProps {}

export const MessagesPage: React.FC<MessagesPageProps> = () => {
    const [showMessagesElements, setShowMessagesElements] = useState(true);
    const { t } = useTranslation();
    const location = useLocation();
    const { path, url } = useRouteMatch();
    const messagingClient = useMessagingClient();
    const dispatch = useDispatch<Dispatch>();
    const { conversationsRequestState, conversations, requestConversations } = useConversations();

    const conversationPath = `${path}/:uuid`;
    const conversationMatch = matchPath<ConversationSubRouteParams>(location.pathname, {
        path: conversationPath,
        exact: false,
    });

    useEffect(() => {
        messagingClient.startWebSocketSubscriptions();
        dispatch.messaging.getMessageResources();
        // DEV: uncomment during dev to see state changes
        // const unsubscribe = messagingClient.subscribe(() => {
        //     console.log('[messaging] state subscriber', messagingClient.getState());
        // });

        // return () => {
        //     unsubscribe();
        // };
    }, [messagingClient, dispatch]);

    useEffect(() => {
        if (!conversationMatch && conversations.length > 0) {
            navigationService.replace(`${url}/${conversations[0].uuid}`);
        }
    }, [conversations, conversationMatch, url]);

    if (conversationsRequestState.state === RequestStateType.ERROR) {
        return (
            <SidebarLayout.Container>
                <SidebarLayout.Content>
                    <GenericError />
                </SidebarLayout.Content>
            </SidebarLayout.Container>
        );
    }

    const loading =
        conversationsRequestState.state === RequestStateType.LOADING && conversations.length === 0;
    const noConversations = !loading && conversations.length === 0;

    if (loading || noConversations) {
        return (
            <SidebarLayout.Container>
                <SidebarLayout.Content>
                    {loading ? <CenteredSpinner /> : <NoConversations />}
                </SidebarLayout.Content>
            </SidebarLayout.Container>
        );
    }

    const showCareCard =
        conversations.filter(c => c.coach?.providerType === ProviderType.COACH).length < 2;

    return (
        <SidebarLayout.Container
            pageTitle={
                showMessagesElements ? (
                    <HeadingText60 marginLeft={30} marginTop={60} marginBottom={30}>
                        {t('messaging:page.title')}
                    </HeadingText60>
                ) : undefined
            }
        >
            {showMessagesElements && (
                <SidebarLayout.Sidebar backgroundColor="transparent">
                    <UnorderedList>
                        {conversations.map(c => (
                            <li key={c.uuid}>
                                <ConversationListItem
                                    conversation={c}
                                    to={`${url}/${c.uuid}`}
                                    active={c.uuid === conversationMatch?.params.uuid}
                                />
                            </li>
                        ))}
                    </UnorderedList>
                    {showCareCard && (
                        <CareCardWrapper>
                            <CareCard
                                title={t('messaging:care_card.title')}
                                titleTranslationKey="messaging:care_card.title"
                                description={t('messaging:care_card.description')}
                                onClick={() => navigationService.navigate('/work-with-a-coach')}
                                images={[coachPlaceholder]}
                            />
                        </CareCardWrapper>
                    )}
                </SidebarLayout.Sidebar>
            )}
            <SidebarLayout.Content>
                <Route
                    path={conversationPath}
                    render={props => (
                        <ConversationSubRoute
                            // Re-mount when the conversation changes
                            key={props.match.params.uuid}
                            goToNextConversation={() => {
                                requestConversations();
                                const nextConvoUuid = conversations.find(
                                    c => c.uuid !== conversationMatch?.params.uuid,
                                )?.uuid;
                                navigationService.navigate(
                                    `/messages${nextConvoUuid ? `/${nextConvoUuid}` : ''}`,
                                );
                            }}
                            setShowMessagesPageElements={setShowMessagesElements}
                        />
                    )}
                    analytics={{ pageName: 'chat-conversation' }}
                />
            </SidebarLayout.Content>
        </SidebarLayout.Container>
    );
};
